import type React from "react";
import { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  InputAdornment,
  Grid,
  CircularProgress,
  Paper,
} from "@mui/material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowBack, Visibility } from "@mui/icons-material";
import { toast } from "react-toastify";
import backgroundImage from "../../assets/backgroundImage.jpg";
import muzukuru from "../../assets/muzukuru.png";
import { motion } from "framer-motion";
import apiService from "../../service/Api";
import { easeOut, easeInOut } from "framer-motion";
import { showSuccessToast } from "../../components/common/ShowToast";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const uidb64 = searchParams.get("uidb64");
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Check if required URL parameters are present
  if (!uidb64 || !token) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="h5" color="error">
          Invalid reset link
        </Typography>
        <Typography variant="body1" color="text.secondary">
          The password reset link is invalid or has expired.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/forgot-password")}
          sx={{ mt: 2 }}
        >
          Request New Reset Link
        </Button>
      </Box>
    );
  }

  const showErrorToast = (message: string, toastId: string = "error-toast") => {
    toast.dismiss(toastId);
    toast.error(message, {
      toastId,
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
      style: {
        backgroundColor: "#fff",
        color: "#333",
        border: "1px solid #C29363",
        zIndex: 9999,
      },
    });
  };

  const validatePassword = (password: string): string | null => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return "Password must be at least 8 characters long.";
    }
    if (!hasUpperCase) {
      return "Password must contain at least one uppercase letter.";
    }
    if (!hasLowerCase) {
      return "Password must contain at least one lowercase letter.";
    }
    if (!hasNumber) {
      return "Password must contain at least one number.";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least one special character.";
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (!newPassword || !confirmPassword) {
      showErrorToast("Please enter both password fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      showErrorToast("Passwords do not match.");
      return;
    }

    const passwordValidationError = validatePassword(newPassword);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      showErrorToast(passwordValidationError);
      return;
    }

    setIsLoading(true);
    try {
      await apiService.post(
        `/api/authentication/reset-password/${uidb64}/${token}/`,
        { new_password: newPassword, confirm_password: confirmPassword },
        true
      );
      setIsPasswordReset(true);
      showSuccessToast("Password reset successfully. Redirecting to login...");
      setTimeout(() => navigate("/login"), 5000);
    } catch (err: any) {
      let errorMessage = "An error occurred. Please try again.";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message; // e.g., "Invalid reset link."
      } else if (err.message?.includes("Network Error")) {
        errorMessage = "Network error. Please check your connection.";
      }
      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const successVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: easeOut },
    },
  };

  return (
    <Grid container sx={{ minHeight: "100vh", overflow: "hidden" }}>
      {/* Left Column - Image Background */}
      <Grid
        size={{ xs: false, md: 6 }}
        sx={{
          position: "relative",
          display: { xs: "none", md: "block" },
          background: `linear-gradient(135deg, rgba(97, 68, 56, 0.8) 0%, rgba(194, 147, 99, 0.6) 100%), url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(45deg, rgba(97, 68, 56, 0.1) 0%, transparent 50%)",
            zIndex: 1,
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            bottom: 40,
            left: 40,
            display: "flex",
            alignItems: "center",
            zIndex: 5,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: "16px",
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <img
                alt="Muzukuru logo"
                width="165px"
                height="70px"
                src={muzukuru}
                style={{ cursor: "pointer" }}
              />
            </Paper>
          </motion.div>
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            zIndex: 3,
            color: "white",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                textShadow: "0 4px 20px rgba(0,0,0,0.3)",
                background:
                  "linear-gradient(45deg, #fff 30%, rgba(194, 147, 99, 1) 90%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Reset Your Password
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                textShadow: "0 2px 10px rgba(0,0,0,0.3)",
                fontWeight: 300,
              }}
            >
              Secure your account with a new password
            </Typography>
          </motion.div>
        </Box>
      </Grid>

      {/* Right Column - Reset Password Form */}
      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, sm: 4, md: 8 },
          py: { xs: 4, md: 0 },
          background: "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 80% 20%, rgba(194, 147, 99, 0.05) 0%, transparent 50%)",
            zIndex: 1,
          },
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ width: "100%", maxWidth: "480px", zIndex: 2 }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(20px)",
              padding: "48px",
              borderRadius: "24px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #614438 0%, #C29363 100%)",
              },
            }}
            component={motion.div}
            whileHover={{ y: -5 }}
            transition={{ duration: 0.3 }}
          >
            {!isPasswordReset ? (
              <Box component="form" onSubmit={handleSubmit}>
                <motion.div variants={itemVariants}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Link
                      to="/login"
                      style={{
                        textDecoration: "none",
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                        marginRight: "auto",
                      }}
                    >
                      <motion.div
                        whileHover={{ x: -5 }}
                        transition={{ duration: 0.2 }}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <ArrowBack sx={{ mr: 1, fontSize: "20px" }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Back to Sign In
                        </Typography>
                      </motion.div>
                    </Link>
                  </Box>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      background:
                        "linear-gradient(135deg, #614438 0%, #C29363 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      textAlign: "center",
                      marginBottom: "8px",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Set New Password
                  </Typography>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#666",
                      textAlign: "center",
                      marginBottom: "40px",
                      fontSize: "16px",
                      lineHeight: 1.6,
                    }}
                  >
                    Enter a new password for your account. Ensure it’s strong
                    and secure.
                  </Typography>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: "#333",
                        fontSize: "14px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      New Password
                    </Typography>
                    <TextField
                      placeholder="Enter your new password"
                      variant="outlined"
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onFocus={() => setFocusedField("newPassword")}
                      onBlur={() => setFocusedField(null)}
                      disabled={isLoading}
                      error={!!passwordError}
                      helperText={passwordError}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <motion.div
                              animate={{
                                rotate: [0, 360],
                                scale: [1, 1.1, 1],
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut",
                              }}
                            >
                              <Visibility
                                sx={{
                                  color:
                                    focusedField === "newPassword"
                                      ? "#614438"
                                      : "#999",
                                  transition: "color 0.3s",
                                }}
                              />
                            </motion.div>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          transition: "all 0.3s ease",
                          height: "56px",
                          "& fieldset": {
                            borderColor: "rgba(0,0,0,0.1)",
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(194, 147, 99, 0.5)",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                            transform: "translateY(-2px)",
                            boxShadow: "0 8px 25px rgba(97, 68, 56, 0.15)",
                            "& fieldset": {
                              borderColor: "#614438",
                            },
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: "#333",
                          fontSize: "16px",
                        },
                      }}
                    />
                  </Box>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: "#333",
                        fontSize: "14px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Confirm Password
                    </Typography>
                    <TextField
                      placeholder="Confirm your new password"
                      variant="outlined"
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onFocus={() => setFocusedField("confirmPassword")}
                      onBlur={() => setFocusedField(null)}
                      disabled={isLoading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <motion.div
                              animate={{
                                rotate: [0, 360],
                                scale: [1, 1.1, 1],
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut",
                              }}
                            >
                              <Visibility
                                sx={{
                                  color:
                                    focusedField === "confirmPassword"
                                      ? "#614438"
                                      : "#999",
                                  transition: "color 0.3s",
                                }}
                              />
                            </motion.div>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          transition: "all 0.3s ease",
                          height: "56px",
                          "& fieldset": {
                            borderColor: "rgba(0,0,0,0.1)",
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(194, 147, 99, 0.5)",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                            transform: "translateY(-2px)",
                            boxShadow: "0 8px 25px rgba(97, 68, 56, 0.15)",
                            "& fieldset": {
                              borderColor: "#614438",
                            },
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: "#333",
                          fontSize: "16px",
                        },
                      }}
                    />
                  </Box>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    sx={{
                      background:
                        "linear-gradient(135deg, #614438 0%, #C29363 100%)",
                      textTransform: "none",
                      padding: "16px",
                      borderRadius: "12px",
                      mb: 3,
                      fontSize: "16px",
                      fontWeight: 600,
                      boxShadow: "0 8px 25px rgba(97, 68, 56, 0.3)",
                      border: "none",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #503629 0%, #A67C52 100%)",
                        boxShadow: "0 12px 35px rgba(97, 68, 56, 0.4)",
                      },
                      "&:disabled": {
                        background: "rgba(97, 68, 56, 0.3)",
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      "Reset Password"
                    )}
                  </Button>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{ color: "#666", fontSize: "15px" }}
                  >
                    Remember your password?{" "}
                    <Link
                      to="/login"
                      style={{
                        textDecoration: "none",
                        fontWeight: 600,
                        background:
                          "linear-gradient(135deg, #C29363 0%, #614438 100%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      <motion.span whileHover={{ scale: 1.05 }}>
                        Sign in
                      </motion.span>
                    </Link>
                  </Typography>
                </motion.div>
              </Box>
            ) : (
              <motion.div
                variants={successVariants}
                initial="hidden"
                animate="visible"
                style={{ textAlign: "center" }}
              >
                <Box sx={{ mb: 3 }}>
                  <motion.div
                    animate={{
                      rotate: [0, 360],
                      scale: [1, 1.1, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: easeInOut,
                    }}
                  >
                    <CheckCircle
                      sx={{
                        fontSize: "80px",
                        color: "#614438",
                        filter: "drop-shadow(0 4px 8px rgba(97, 68, 56, 0.3))",
                      }}
                    />
                  </motion.div>
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    background:
                      "linear-gradient(135deg, #614438 0%, #C29363 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    mb: 2,
                  }}
                >
                  Password Reset Successful
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: "#666",
                    mb: 4,
                    fontSize: "16px",
                    lineHeight: 1.6,
                  }}
                >
                  Your password has been updated. You can now sign in with your
                  new password.
                </Typography>

                <Link
                  to="/login"
                  style={{
                    textDecoration: "none",
                    fontWeight: 600,
                    background:
                      "linear-gradient(135deg, #C29363 0%, #614438 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: "15px",
                  }}
                >
                  <motion.span whileHover={{ scale: 1.05 }}>
                    ← Back to Sign In
                  </motion.span>
                </Link>
              </motion.div>
            )}
          </Paper>
        </motion.div>
      </Grid>
    </Grid>
  );
};

export default ResetPassword;
